import { useCallback, useEffect } from "react";
import { useSelector, useDispatch } from "react-redux";
import {
  selectOrganizationPlan,
  selectIsAuthenticated,
  shouldFetchOrganizationPlan,
  fetchOrganizationPlanThunk,
} from "@/features/auth/store/sessionSlice";
import { PlanFeatures } from "@/shared/types/planFeatures";
import { getContentFeatureAvailability } from "@/shared/utils/contentFeatureAvailability.utils";
import { RootState, AppDispatch } from "@/store";

/**
 * Custom hook to manage organization features state
 * Automatically fetches features if missing and user is authenticated
 *
 * @param autoFetch - Whether to automatically fetch features if missing (default: true)
 * @returns Object containing features data and loading state
 */
export const useOrganizationPlan = (autoFetch = true) => {
  const dispatch = useDispatch<AppDispatch>();
  const isAuthenticated = useSelector((state: RootState) =>
    selectIsAuthenticated(state),
  );
  const organizationPlan = useSelector((state: RootState) =>
    selectOrganizationPlan(state),
  );
  const organizationId = useSelector((state: RootState) =>
    shouldFetchOrganizationPlan(state),
  );

  const planError = useSelector((state: RootState) =>
    state.session.error?.code === "plan_fetch_error" ? state.session.error : null,
  );
  // `organizationId` ne vaut que tant que le plan est absent : refetch() doit aussi
  // pouvoir recharger un plan déjà en Redux, il lit donc l'identifiant à la source.
  const sessionOrganizationId = useSelector(
    (state: RootState) => state.session.user?.representative?.organization_id,
  );

  const refetch = useCallback(() => {
    if (sessionOrganizationId) {
      dispatch(fetchOrganizationPlanThunk(sessionOrganizationId));
    }
  }, [dispatch, sessionOrganizationId]);

  useEffect(() => {
    // Only fetch if autoFetch is enabled, user is authenticated, and we have an organization ID to fetch
    if (autoFetch && isAuthenticated && organizationId) {
      dispatch(fetchOrganizationPlanThunk(organizationId));
    }
  }, [dispatch, autoFetch, isAuthenticated, organizationId]);

  return {
    plan: organizationPlan,
    isLoading: isAuthenticated && !organizationPlan && !!organizationId && !planError,
    hasOrganizationPlan: !!organizationPlan,
    refetch,
  };
};

// Helper functions to extract features by category
const getEmployeeFeatures = (features?: PlanFeatures) => ({
  showEmployeeEmail: !!features?.show_employee_email,
  showEmployeePhone: !!features?.show_employee_phone,
  showEmployeeCredentialSentAt: !!features?.show_employee_credential_sent_at,
});

const getCoreServiceFeatures = (features?: PlanFeatures) => ({
  organizationParameters: !!features?.organization_parameters,
  serviceRepresentatives: !!features?.service_representatives,
  serviceRepresentativesDirectory: !!features?.service_representative_directory,
  serviceEmployees: !!features?.service_employees,
  serviceVouchers: !!features?.service_vouchers,
  serviceDeals: !!features?.service_deals,
  serviceTasks: !!features?.service_tasks,
  serviceJobs: !!features?.service_jobs,
  serviceRefundReattribution: !!features?.service_refund_reattribution,
});

const getCommunicationFeatures = (features?: PlanFeatures) => ({
  serviceSms: !!features?.service_sms,
  servicePush: !!features?.service_push,
  serviceExternalContacts: !!features?.service_external_contacts,
  serviceCalendar: !!features?.service_calendar,
  serviceExchanges: !!features?.service_exchanges,
  serviceCommunication: !!features?.service_communication,
  serviceChat: !!features?.service_chat || !!features?.service_chat_legacy,
});

const getContentFeatures = (features?: PlanFeatures) => ({
  serviceBlog: !!features?.service_blog,
  servicePinnednotes: !!features?.service_pinnednotes,
  servicePolls: getContentFeatureAvailability(features).polls,
  serviceSurveys: getContentFeatureAvailability(features).surveys,
  serviceNewsletter: !!features?.service_newsletter,
  serviceChangelog: !!features?.service_changelog,
});

const getAdvancedFeatures = (features?: PlanFeatures) => ({
  serviceLottery: !!features?.service_lottery,
  serviceProofDocuments:
    !!features?.service_proof_document_legacy ||
    !!features?.service_proof_document,
  serviceTicketing: !!features?.service_ticketing,
  serviceBeneficiaries: !!features?.service_beneficiaries,
  serviceCustomAttribute: !!features?.service_custom_attribute,
  serviceDrive: !!features?.service_drive,
  serviceUsergroups: !!features?.service_usergroups,
  serviceDynamicGroup: !!features?.service_dynamic_group,
  servicePreferences: !!features?.service_preferences,
  customForms: !!features?.custom_forms,
  rulesEngine: !!features?.rules_engine,
  serviceDashboardLayouts: !!features?.service_dashboard_layouts,
  serviceNavigationLayout: !!features?.service_navigation_layout,
  serviceTheme: !!features?.service_theme,
  serviceCompta: !!features?.service_compta,
  serviceBridge: !!features?.service_bridge,
  serviceSubsidiesRulesEngine: !!features?.service_subsidies_rules_engine,
  serviceSuggestions: !!features?.user_suggestions,
  serviceRepresentativeRoles: !!features?.service_representative_roles,
  serviceClassifieds: !!features?.service_classifieds,
  serviceAdventCalendar: !!features?.service_advent_calendar,
});

/**
 * Hook to access organization plan features
 * @returns Object containing feature flags from the organization plan
 */
export const useOrganizationFeatures = () => {
  const { plan, isLoading: isPlanLoading } = useOrganizationPlan();
  const features = plan?.features;

  return {
    isPlanLoading,
    ...getEmployeeFeatures(features),
    ...getCoreServiceFeatures(features),
    ...getCommunicationFeatures(features),
    ...getContentFeatures(features),
    ...getAdvancedFeatures(features),
    plan,
  };
};

/**
 * Hook to manually fetch organization features
 * Useful when you want to control when to fetch features
 */
export const useOrganizationPlanFetcher = () => {
  const dispatch = useDispatch<AppDispatch>();
  const organizationId = useSelector((state: RootState) =>
    shouldFetchOrganizationPlan(state),
  );

  return {
    fetchOrganizationPlan: () => {
      if (organizationId) {
        return dispatch(fetchOrganizationPlanThunk(organizationId));
      }
      return null;
    },
    canFetch: !!organizationId,
  };
};

export default useOrganizationPlan;
